/**
 * Public Tenant Signup Page (Module 2.1 Frontend Phase 1 Part 1).
 *
 * Self-service registration that drives the backend's signup flow
 * shipped in Module 2.1 Phase 2 (P3+P4):
 *
 *   GET  /auth/check-slug   live slug availability + suggestions
 *   POST /auth/register-tenant   the actual signup
 *
 * Adds inline UX over the previous version:
 *   - Live slug check with bilingual status badge + clickable
 *     suggestions when the chosen slug is taken or reserved.
 *   - Password-strength meter + rules checklist that mirrors the
 *     backend rules verbatim (12+ chars, letter, digit, block-list).
 *   - Structured 422 errors from Pydantic mapped back to the field
 *     that triggered them — no more opaque toast strings.
 *   - Submit disabled until every rule passes.
 *
 * The redirect-to-dashboard flow is unchanged: signup → save JWT →
 * fetch /auth/me → push to /dashboard.
 */
import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Building2, Mail, Lock, User as UserIcon, Hash, Loader2, ArrowLeft,
  CheckCircle2, XCircle, AlertCircle, Eye, EyeOff,
} from 'lucide-react'

import { authApi } from '@/api/auth'
import { useAuthStore } from '@/stores/authStore'
import { getErrorMessage } from '@/lib/api'
import { setTokens as setStoredTokens } from '@/lib/tokenStorage'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { useCheckSlug } from '@/hooks/useCheckSlug'
import {
  getPasswordStrength, PASSWORD_MIN_LENGTH,
} from '@/utils/passwordStrength'


type FieldErrors = Partial<{
  company_name_en: string
  company_name_ar: string
  slug: string
  admin_email: string
  admin_username: string
  admin_full_name: string
  admin_password: string
}>


/** Pull a 422 response from axios and bucket each error by field. */
function pickFieldErrors(error: unknown): FieldErrors {
  const detail = (error as {
    response?: { status?: number; data?: { detail?: unknown } }
  } | null)?.response

  if (detail?.status !== 422 || !Array.isArray(detail.data?.detail)) {
    return {}
  }
  const out: FieldErrors = {}
  for (const item of detail.data!.detail as Array<{
    loc?: unknown[]
    msg?: string
  }>) {
    const last = item.loc?.[item.loc.length - 1]
    if (typeof last !== 'string') continue
    if (last in (out as Record<string, unknown>)) continue  // first wins
    ;(out as Record<string, string>)[last] = item.msg || 'Invalid value'
  }
  return out
}


export default function SignupPage() {
  const { i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'

  // Tenant fields
  const [companyNameEn, setCompanyNameEn] = useState('')
  const [companyNameAr, setCompanyNameAr] = useState('')
  const [slug, setSlug] = useState('')
  const [slugAutoFilled, setSlugAutoFilled] = useState(true)

  // Admin fields
  const [adminFullName, setAdminFullName] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminUsername, setAdminUsername] = useState('')
  const [adminPassword, setAdminPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)

  // Auto-generate slug from English company name (until user manually edits)
  useEffect(() => {
    if (slugAutoFilled) {
      const generated = companyNameEn
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .substring(0, 64)
      setSlug(generated)
    }
  }, [companyNameEn, slugAutoFilled])

  const handleSlugChange = (value: string) => {
    setSlugAutoFilled(false)
    setSlug(value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
  }

  // Live signals
  const slugCheck = useCheckSlug(slug)
  const strength = useMemo(
    () => getPasswordStrength(adminPassword, isArabic ? 'ar' : 'en'),
    [adminPassword, isArabic],
  )

  const slugOk = slugCheck.state === 'available'
  const canSubmit =
    !isLoading
    && companyNameEn.length >= 2
    && slug.length >= 3
    && adminFullName.length >= 2
    && adminEmail.length > 0
    && adminUsername.length >= 3
    && slugOk
    && strength.valid

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setFieldErrors({})
    setIsLoading(true)

    try {
      const result = await authApi.registerTenant({
        company_name_en: companyNameEn,
        company_name_ar: companyNameAr || null,
        slug,
        admin_email: adminEmail,
        admin_username: adminUsername,
        admin_full_name: adminFullName,
        admin_password: adminPassword,
      })

      // Store the token via the centralized backend BEFORE /auth/me so the
      // axios interceptor attaches it. setAuth() below re-persists it too.
      setStoredTokens(result.access_token, null)
      const user = await authApi.me()
      setAuth(user, result.access_token)

      toast.success(
        isArabic
          ? `أهلاً بك في Injez، ${result.tenant_slug}!`
          : `Welcome to Injez, ${result.tenant_slug}!`,
      )
      // Land on the verification-pending screen (Phase 1 P2) so the
      // user knows to check their inbox. The page has a "Continue
      // to dashboard" button — verification itself is non-blocking.
      navigate('/verify-email-pending', {
        state: { email: result.admin_email },
      })
    } catch (error) {
      const fe = pickFieldErrors(error)
      if (Object.keys(fe).length > 0) {
        setFieldErrors(fe)
        toast.error(
          isArabic
            ? 'تحقق من الحقول المظللة باللون الأحمر'
            : 'Please fix the highlighted fields',
        )
      } else {
        toast.error(getErrorMessage(error))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const slugBadge = renderSlugBadge(slugCheck, isArabic)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4 py-8">
      <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-700/10 rounded-full blur-3xl" />

      <div className="relative w-full max-w-2xl">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-black rounded-2xl shadow-2xl mb-4 p-3">
            <img src="/logo.png" alt="Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Injez ERP</h1>
          <p className="text-gray-400">
            {isArabic
              ? 'إدارة مشاريع البناء لدول الخليج'
              : 'Construction project management for the GCC'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {isArabic ? 'أنشئ حسابك' : 'Create your account'}
            </h2>
            <p className="text-gray-600">
              {isArabic
                ? 'ابدأ بفترة تجريبية مجانية لمدة 30 يوماً'
                : 'Get started with a 30-day free trial'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="border-b border-gray-200 pb-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                {isArabic ? 'معلومات الشركة' : 'Company Information'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label={isArabic ? 'اسم الشركة (إنجليزي) *' : 'Company Name (English) *'}
                  icon={<Building2 className="w-5 h-5 text-gray-400" />}
                  error={fieldErrors.company_name_en}
                >
                  <input
                    type="text"
                    value={companyNameEn}
                    onChange={(e) => setCompanyNameEn(e.target.value)}
                    placeholder="ACME Construction LLC"
                    className={inputClass}
                    required
                    minLength={2}
                    maxLength={255}
                    dir="ltr"
                  />
                </Field>

                <Field
                  label={isArabic
                    ? 'اسم الشركة (عربي) - اختياري'
                    : 'Company Name (Arabic) - Optional'}
                  icon={<Building2 className="w-5 h-5 text-gray-400" />}
                  error={fieldErrors.company_name_ar}
                >
                  <input
                    type="text"
                    value={companyNameAr}
                    onChange={(e) => setCompanyNameAr(e.target.value)}
                    placeholder="شركة أكمي للمقاولات"
                    className={inputClass}
                    maxLength={255}
                    dir="rtl"
                  />
                </Field>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isArabic ? 'معرّف URL *' : 'URL Slug *'}
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={slug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    placeholder="acme-construction"
                    className={inputClass + ' font-mono text-sm pe-32'}
                    required
                    minLength={3}
                    maxLength={64}
                    pattern="^[a-z0-9-]+$"
                    dir="ltr"
                    aria-invalid={
                      slugCheck.state === 'unavailable'
                      || slugCheck.state === 'invalid'
                    }
                  />
                  {slugBadge && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      {slugBadge}
                    </div>
                  )}
                </div>

                <SlugFeedback
                  check={slugCheck}
                  isArabic={isArabic}
                  onPickSuggestion={(s) => {
                    setSlug(s)
                    setSlugAutoFilled(false)
                  }}
                />

                {fieldErrors.slug && (
                  <p className="mt-1 text-xs text-rose-600">
                    {fieldErrors.slug}
                  </p>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                {isArabic ? 'حساب المدير' : 'Admin Account'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label={isArabic ? 'الاسم الكامل *' : 'Full Name *'}
                  icon={<UserIcon className="w-5 h-5 text-gray-400" />}
                  error={fieldErrors.admin_full_name}
                >
                  <input
                    type="text"
                    value={adminFullName}
                    onChange={(e) => setAdminFullName(e.target.value)}
                    placeholder="John Smith"
                    className={inputClass}
                    required
                    minLength={2}
                    maxLength={200}
                  />
                </Field>

                <Field
                  label={isArabic ? 'اسم المستخدم *' : 'Username *'}
                  icon={<UserIcon className="w-5 h-5 text-gray-400" />}
                  error={fieldErrors.admin_username}
                >
                  <input
                    type="text"
                    value={adminUsername}
                    onChange={(e) => setAdminUsername(e.target.value)}
                    placeholder="jsmith"
                    className={inputClass}
                    required
                    minLength={3}
                    maxLength={50}
                    dir="ltr"
                  />
                </Field>
              </div>

              <div className="mt-4">
                <Field
                  label={isArabic ? 'البريد الإلكتروني *' : 'Email *'}
                  icon={<Mail className="w-5 h-5 text-gray-400" />}
                  error={fieldErrors.admin_email}
                >
                  <input
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="admin@acme-construction.com"
                    className={inputClass}
                    required
                    dir="ltr"
                  />
                </Field>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isArabic ? 'كلمة المرور *' : 'Password *'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder={
                      isArabic
                        ? `${PASSWORD_MIN_LENGTH} حرفاً على الأقل`
                        : `At least ${PASSWORD_MIN_LENGTH} characters`
                    }
                    className={inputClass + ' pe-12'}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    dir="ltr"
                    aria-invalid={
                      adminPassword.length > 0 && !strength.valid
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={
                      showPassword
                        ? (isArabic ? 'إخفاء كلمة المرور' : 'Hide password')
                        : (isArabic ? 'إظهار كلمة المرور' : 'Show password')
                    }
                  >
                    {showPassword
                      ? <EyeOff className="w-5 h-5" />
                      : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {fieldErrors.admin_password && (
                  <p className="mt-1 text-xs text-rose-600">
                    {fieldErrors.admin_password}
                  </p>
                )}

                <PasswordStrengthMeter
                  strength={strength}
                  isArabic={isArabic}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 mt-6"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>
                    {isArabic ? 'جارٍ إنشاء الحساب...' : 'Creating your account...'}
                  </span>
                </>
              ) : (
                <span>
                  {isArabic
                    ? 'أنشئ الحساب وابدأ'
                    : 'Create Account & Get Started'}
                </span>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-gray-600 text-sm">
              {isArabic ? 'لديك حساب بالفعل؟' : 'Already have an account?'}{' '}
              <Link
                to="/login"
                className="text-red-600 hover:text-red-700 hover:underline font-semibold inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
                {isArabic ? 'سجّل الدخول' : 'Sign in'}
              </Link>
            </p>
          </div>

          <div className="mt-4 flex justify-center">
            <LanguageSwitcher />
          </div>
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          Injez ERP © 2026
        </p>
      </div>
    </div>
  )
}


// ─────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────

const inputClass =
  'w-full ps-11 pe-4 py-3 border border-gray-300 rounded-lg '
  + 'focus:outline-none focus:ring-2 focus:ring-red-500 '
  + 'focus:border-transparent'


function Field(props: {
  label: string
  icon: React.ReactNode
  error?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {props.label}
      </label>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {props.icon}
        </div>
        {props.children}
      </div>
      {props.error && (
        <p className="mt-1 text-xs text-rose-600">{props.error}</p>
      )}
    </div>
  )
}


function renderSlugBadge(
  check: ReturnType<typeof useCheckSlug>,
  isArabic: boolean,
): React.ReactNode {
  if (check.state === 'idle') return null
  if (check.state === 'checking') {
    return (
      <span className="text-xs text-gray-400 inline-flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" />
        {isArabic ? 'جارٍ التحقق' : 'Checking'}
      </span>
    )
  }
  if (check.state === 'available') {
    return (
      <span className="text-xs text-emerald-600 inline-flex items-center gap-1 font-semibold">
        <CheckCircle2 className="w-4 h-4" />
        {isArabic ? 'متاح' : 'Available'}
      </span>
    )
  }
  if (check.state === 'unavailable') {
    const reserved = check.data?.reason === 'slug is reserved'
    return (
      <span className="text-xs text-rose-600 inline-flex items-center gap-1 font-semibold">
        <XCircle className="w-4 h-4" />
        {reserved
          ? (isArabic ? 'محجوز' : 'Reserved')
          : (isArabic ? 'غير متاح' : 'Taken')}
      </span>
    )
  }
  // invalid
  return (
    <span className="text-xs text-amber-600 inline-flex items-center gap-1 font-semibold">
      <AlertCircle className="w-4 h-4" />
      {isArabic ? 'صيغة غير صحيحة' : 'Invalid'}
    </span>
  )
}


function SlugFeedback(props: {
  check: ReturnType<typeof useCheckSlug>
  isArabic: boolean
  onPickSuggestion: (s: string) => void
}) {
  const { check, isArabic, onPickSuggestion } = props
  if (check.state === 'invalid' && check.data?.reason) {
    return (
      <p className="mt-1 text-xs text-amber-700">{check.data.reason}</p>
    )
  }
  if (check.state === 'unavailable') {
    const reasonText = check.data?.reason === 'slug is reserved'
      ? (isArabic
          ? 'هذا المعرّف محجوز للنظام. جرّب أحد البدائل:'
          : "That slug is reserved. Try one of these:")
      : (isArabic ? 'هذا المعرّف مستخدم. جرّب:' : 'Slug taken. Try:')
    return (
      <div className="mt-2">
        <p className="text-xs text-gray-600 mb-1">{reasonText}</p>
        <div className="flex flex-wrap gap-2">
          {check.data?.suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onPickSuggestion(s)}
              className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded font-mono"
              dir="ltr"
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    )
  }
  return (
    <p className="mt-1 text-xs text-gray-500">
      {isArabic
        ? 'حروف صغيرة وأرقام وشرطات. هذا جزء من رابط النظام.'
        : 'Lowercase letters, digits, and hyphens only. This forms part of your URL.'}
    </p>
  )
}


function PasswordStrengthMeter(props: {
  strength: ReturnType<typeof getPasswordStrength>
  isArabic: boolean
}) {
  const { strength, isArabic } = props

  const barClass = (idx: number) => {
    if (idx >= strength.score) return 'bg-gray-200'
    if (strength.score <= 1) return 'bg-rose-500'
    if (strength.score === 2) return 'bg-amber-500'
    if (strength.score === 3) return 'bg-blue-500'
    return 'bg-emerald-500'
  }

  const labelText = (() => {
    if (strength.label === 'empty') return ''
    const en = {
      weak: 'Weak', fair: 'Fair', good: 'Good', strong: 'Strong',
    } as const
    const ar = {
      weak: 'ضعيفة', fair: 'مقبولة', good: 'جيدة', strong: 'قوية',
    } as const
    const key = strength.label as 'weak' | 'fair' | 'good' | 'strong'
    return isArabic ? ar[key] : en[key]
  })()

  return (
    <div className="mt-2">
      <div className="flex items-center gap-1.5 mb-2">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${barClass(i)}`}
          />
        ))}
        {labelText && (
          <span className="text-xs text-gray-600 ms-2 min-w-[3rem]">
            {labelText}
          </span>
        )}
      </div>
      <ul className="space-y-1">
        {strength.rules.map((rule) => (
          <li
            key={rule.id}
            className={
              'text-xs flex items-center gap-1.5 '
              + (rule.passes ? 'text-emerald-700' : 'text-gray-500')
            }
          >
            {rule.passes
              ? <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
              : <XCircle className="w-3 h-3 flex-shrink-0" />}
            <span>{rule.message}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
